import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  Code, 
  Copy, 
  Check, 
  ArrowLeft, 
  RotateCcw, 
  Globe, 
  Smartphone, 
  Monitor, 
  CheckCircle2, 
  AlertTriangle 
} from 'lucide-react';

export const MetaTagGenerator = () => {
  const { withAd, toast } = useAdRedirect();
  const [title, setTitle] = useState('Ultimate SaaS Dashboard - Fast & Secure');
  const [description, setDescription] = useState('Accelerate your team productivity with our enterprise-grade analytics platform. Zero setup required, secure cloud infrastructure, and 24/7 expert support.');
  const [keywords, setKeywords] = useState('saas dashboard, analytics platform, cloud productivity, secure tools');
  const [author, setAuthor] = useState('Toolmetrico Inc');
  const [robotsIndex, setRobotsIndex] = useState('index');
  const [robotsFollow, setRobotsFollow] = useState('follow');
  const [language, setLanguage] = useState('en');
  const [deviceView, setDeviceView] = useState('desktop'); // 'desktop' | 'mobile'
  const [copied, setCopied] = useState(false);

  // Generate Meta Tag code string
  const generatedCode = `<!-- Primary Meta Tags -->
<title>${title || 'Website Title'}</title>
<meta name="title" content="${title || 'Website Title'}">
<meta name="description" content="${description || ''}">
<meta name="keywords" content="${keywords || ''}">
<meta name="author" content="${author || ''}">
<meta name="robots" content="${robotsIndex}, ${robotsFollow}">
<meta http-equiv="Content-Type" content="text/html; charset=utf-8">
<meta name="language" content="${language === 'en' ? 'English' : language === 'es' ? 'Spanish' : language === 'fr' ? 'French' : language === 'de' ? 'German' : 'English'}">
<meta name="viewport" content="width=device-width, initial-scale=1">`;

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleReset = () => {
    setTitle('');
    setDescription('');
    setKeywords('');
    setAuthor('');
    setRobotsIndex('index');
    setRobotsFollow('follow');
    setLanguage('en');
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', paddingBottom: '60px' }}>
      <SEO 
        title="Free Meta Tag Generator - HTML SEO Tags" 
        description="Create perfect HTML meta tags for Google and search engines. Custom title, description, keywords, and robot crawling tags with live SERP preview."
        keywords="meta tag generator, html meta tags, generate meta tags, seo meta tags, google serp preview, meta description generator"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '0.9rem' }}>
        <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Meta Tag Generator</span>
      </div>

      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
          HTML Meta Tag Generator & SERP Preview
        </h1>
        <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0 }}>
          Generate highly optimized metadata tags to ensure your webpages render flawlessly on Google Search results and social crawlers.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '32px' }} className="tool-grid-split">
        
        {/* Left Side: Inputs */}
        <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)', alignSelf: 'start' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>Meta Configurations</h3>
            <button onClick={handleReset} style={{ background: 'rgba(15, 23, 42, 0.04)', border: 'none', padding: '6px 12px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <RotateCcw size={14} /> Clear All
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Title */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                <span>Site Title Tag</span>
                <span style={{ color: title.length > 60 ? '#ef4444' : title.length > 30 ? '#10b981' : 'var(--text-secondary)' }}>{title.length} / 60 chars</span>
              </div>
              <input 
                type="text" 
                value={title} 
                onChange={(e) => setTitle(e.target.value)}
                placeholder="E.g. Toolmetrico - Free Developer Suite"
                style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.95rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
              />
              {title.length > 60 && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#ef4444', fontSize: '0.75rem', marginTop: '6px', fontWeight: 600 }}>
                  <AlertTriangle size={12} /> Google typically truncates titles longer than 60 characters.
                </span>
              )}
            </div>

            {/* Description */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                <span>Meta Description</span>
                <span style={{ color: description.length > 160 ? '#ef4444' : description.length > 120 ? '#10b981' : 'var(--text-secondary)' }}>{description.length} / 160 chars</span>
              </div>
              <textarea 
                rows="4"
                value={description} 
                onChange={(e) => setDescription(e.target.value)}
                placeholder="E.g. Download and convert tools online instantly..."
                style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.95rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box', resize: 'vertical' }}
              />
              {description.length > 160 && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#ef4444', fontSize: '0.75rem', marginTop: '6px', fontWeight: 600 }}>
                  <AlertTriangle size={12} /> Snippets over 160 characters risk getting truncated on SERPs.
                </span>
              )}
            </div>

            {/* Keywords */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Keywords (Comma Separated)</label>
              <input 
                type="text" 
                value={keywords} 
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="seo tools, meta tag generator, html tags"
                style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.95rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
              />
            </div>

            {/* Author & Language */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Author / Company</label>
                <input 
                  type="text" 
                  value={author} 
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder="Company Name"
                  style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.95rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Primary Language</label>
                <select 
                  value={language} 
                  onChange={(e) => setLanguage(e.target.value)}
                  style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.95rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                >
                  <option value="en">English (en)</option>
                  <option value="es">Spanish (es)</option>
                  <option value="fr">French (fr)</option>
                  <option value="de">German (de)</option>
                </select>
              </div>
            </div>

            {/* Robots Directives */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Robots Indexing</label>
                <select 
                  value={robotsIndex} 
                  onChange={(e) => setRobotsIndex(e.target.value)}
                  style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.95rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                >
                  <option value="index">Index (Allow Crawling)</option>
                  <option value="noindex">Noindex (Block Spiders)</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Robots Following</label>
                <select 
                  value={robotsFollow} 
                  onChange={(e) => setRobotsFollow(e.target.value)}
                  style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.95rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                >
                  <option value="follow">Follow (Pass Links)</option>
                  <option value="nofollow">Nofollow (Do Not Pass)</option>
                </select>
              </div>
            </div>

          </div>
        </div>

        {/* Right Side: Output Code + Live SERP Preview */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          {/* Output Code Card */}
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Code size={18} style={{ color: 'var(--primary)' }} /> Generated HTML Meta Tags
              </h3>
              <AdToast message={toast} />
              <button 
                onClick={withAd('meta-tag-copy', handleCopy)}
                style={{ background: copied ? '#10b981' : 'var(--primary)', color: '#ffffff', border: 'none', padding: '8px 16px', borderRadius: '10px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', transition: 'var(--transition-smooth)' }}
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? 'Copied Code!' : 'Copy Tags'}
              </button>
            </div>

            <pre style={{ background: '#0f172a', color: '#e2e8f0', padding: '20px', borderRadius: '14px', fontSize: '0.85rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflowX: 'auto', margin: 0, border: '1px solid rgba(255, 255, 255, 0.1)', lineHeight: 1.6 }}>
              {generatedCode}
            </pre>
          </div>

          {/* Live SERP Preview Card */}
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Globe size={18} style={{ color: '#10b981' }} /> Live Google Search Preview
              </h3>

              <div style={{ display: 'flex', gap: '6px', background: 'var(--bg-secondary)', padding: '4px', borderRadius: '10px' }}>
                <button 
                  onClick={() => setDeviceView('desktop')}
                  style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', background: deviceView === 'desktop' ? '#ffffff' : 'transparent', color: deviceView === 'desktop' ? 'var(--text-primary)' : 'var(--text-secondary)', boxShadow: deviceView === 'desktop' ? 'var(--shadow-xs)' : 'none', transition: 'var(--transition-smooth)' }}
                >
                  <Monitor size={14} /> Desktop
                </button>
                <button 
                  onClick={() => setDeviceView('mobile')}
                  style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', background: deviceView === 'mobile' ? '#ffffff' : 'transparent', color: deviceView === 'mobile' ? 'var(--text-primary)' : 'var(--text-secondary)', boxShadow: deviceView === 'mobile' ? 'var(--shadow-xs)' : 'none', transition: 'var(--transition-smooth)' }}
                >
                  <Smartphone size={14} /> Mobile
                </button>
              </div>
            </div>

            {/* Google SERP Simulated Container */}
            <div style={{ 
              background: '#ffffff', 
              padding: deviceView === 'desktop' ? '24px' : '20px', 
              borderRadius: '16px', 
              border: '1px solid #e2e8f0', 
              maxWidth: deviceView === 'desktop' ? '650px' : '360px',
              margin: '0 auto',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)'
            }}>
              <div style={{ fontSize: '0.8rem', color: '#475569', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 700 }}>Sponsored</span>
                <span>https://example.com</span>
              </div>

              <h4 style={{ fontSize: deviceView === 'desktop' ? '1.25rem' : '1.1rem', fontWeight: 500, color: '#1a0dab', margin: '4px 0', textDecoration: 'none', fontFamily: 'Arial, sans-serif', cursor: 'pointer', lineHeight: 1.3 }}>
                {title ? (title.length > 60 ? title.substring(0, 60) + '...' : title) : 'Website Title'}
              </h4>

              <p style={{ fontSize: '0.85rem', color: '#4d5156', margin: 0, lineHeight: 1.5, fontFamily: 'Arial, sans-serif' }}>
                {description ? (description.length > 160 ? description.substring(0, 160) + '...' : description) : 'No meta description provided. Google will extract random snippet sentences from your webpage text.'}
              </p>
            </div>
          </div>

        </div>

      </div>

      <style>{`
        @media (min-width: 992px) {
          .tool-grid-split { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      <AdUnit />
      <ToolPageExtras toolId="meta-tag-generator" />
    </div>
  );
};

export default MetaTagGenerator;
